/**
 * RouteMetriek — één grammatica voor de drie beperkingen van een routedag:
 * ophalen, afleveren en dagduur. Elke rij is label, waarde/totaal, balk.
 *
 * Waarom (besluit Willem 08-09-2026): de kaart droeg drie beperkingen in drie
 * notaties, met suffixen die de breuk ernaast herhaalden ("↑15/14 1 te veel").
 * Op smalle kaarten brak dat middenin af. Eén vorm laat je in één blik zien
 * welke van de drie het knelpunt is.
 *
 * Kleur zit alleen in de balk; het cijfer wordt rood zodra het écht over de
 * grens gaat. Zo blijft een volle-maar-niet-overboekte route rustig leesbaar.
 */
import clsx from 'clsx';
import type { MinutenStandType } from '../../lib/salesforce/types';

export type MetriekKleur = 'ok' | 'krap' | 'over';

/** "38 min" of "1u20"; negatief wordt "-12 min". */
function minLabel(min: number): string {
  const abs = Math.abs(Math.round(min));
  const u = Math.floor(abs / 60);
  const m = abs % 60;
  const t = u === 0 ? `${m} min` : m === 0 ? `${u}u` : `${u}u${String(m).padStart(2, '0')}`;
  return min < 0 ? `-${t}` : t;
}

/**
 * Merkteken van de minuten-poort naast de stoptelling: `dichtbij` = alleen
 * adressen die weinig minuten kosten passen nog; `dicht op tijd` = ook dat
 * niet. Open of onbekend toont niets; de stoptelling zegt dan genoeg.
 */
export function minutenMerk(stand: MinutenStandType | null | undefined): { merk: React.ReactNode; title: string } | null {
  if (!stand || stand.uitkomst === 'open' || stand.uitkomst === 'onbekend') return null;
  const rest = stand.restMin == null ? '' : stand.restMin >= 0 ? `nog ${minLabel(stand.restMin)} in de dag` : `${minLabel(stand.restMin)} boven budget`;
  const reserve = stand.reserveMin > 0 ? `, reserve ander type ${minLabel(stand.reserveMin)}` : '';
  if (stand.uitkomst === 'dichtbij') {
    return {
      merk: <span className="text-[9px] text-amber-700 bg-amber-50 px-1 py-0.5 rounded whitespace-nowrap">dichtbij</span>,
      title: `Alleen adressen vlakbij de route worden nog aangeboden (${rest}${reserve})`,
    };
  }
  return {
    merk: <span className="text-[9px] text-red-700 bg-red-50 px-1 py-0.5 rounded whitespace-nowrap">dicht op tijd</span>,
    title: `Geen plek meer op tijd (${rest}${reserve}); handmatig plaatsen kan altijd`,
  };
}

const BALK: Record<MetriekKleur, string> = {
  ok: 'bg-emerald-500',
  krap: 'bg-amber-500',
  over: 'bg-red-500',
};

/**
 * Groen tot 85%, amber tot de grens, rood erboven.
 * `gelijkIsVol`: bij capaciteit is precies vol al rood (er kan niets meer bij);
 * bij tijd is exact op het budget nog niet "boven budget".
 */
export function metriekKleur(
  waarde: number | null | undefined,
  totaal: number | null | undefined,
  gelijkIsVol = false
): MetriekKleur | null {
  if (waarde == null || totaal == null || !Number.isFinite(waarde) || !Number.isFinite(totaal) || totaal <= 0) {
    return null;
  }
  const pct = waarde / totaal;
  if (gelijkIsVol ? pct >= 1 : pct > 1) return 'over';
  if (pct >= 0.85) return 'krap';
  return 'ok';
}

export function metriekPercentage(waarde: number | null | undefined, totaal: number | null | undefined): number {
  if (waarde == null || totaal == null || totaal <= 0) return 0;
  return Math.min(100, Math.max(0, Math.round((waarde / totaal) * 100)));
}

interface MetriekRijProps {
  label: string;
  /** Voorgevormde waarde: "14" of "9u18". */
  waarde: string;
  /** Voorgevormd totaal: "14" of "10u"; null laat de breuk weg. */
  totaal: string | null;
  pct: number;
  kleur: MetriekKleur | null;
  /** Cijfer rood: alleen als de waarde de grens echt passeert. */
  overschreden?: boolean;
  title?: string;
  /** Klein merkteken rechts van het label (bijv. "wordt doorgerekend"). */
  merk?: React.ReactNode;
}

export function MetriekRij({ label, waarde, totaal, pct, kleur, overschreden = false, title, merk }: MetriekRijProps) {
  return (
    <div className="flex items-center gap-2 text-[11px]" title={title}>
      <span className="w-[52px] shrink-0 text-gray-500 truncate">{label}</span>
      <span className="w-[62px] shrink-0 text-right tabular-nums whitespace-nowrap">
        <span className={clsx('font-semibold', overschreden ? 'text-red-700' : 'text-gray-900')}>{waarde}</span>
        {totaal != null && <span className="text-gray-400">/{totaal}</span>}
      </span>
      <span className="flex-1 h-1.5 min-w-[24px] rounded-full bg-gray-200 overflow-hidden">
        <span className={clsx('block h-full rounded-full', kleur ? BALK[kleur] : 'bg-gray-300')} style={{ width: `${pct}%` }} />
      </span>
      {merk}
    </div>
  );
}
